import { authApi } from "@/features/auth/auth.api";
import { ensureMockAuthSession } from "@/features/auth/auth.mock";
import { useAuthStore, waitForAuthHydration } from "@/features/auth/auth.store";
import { userApi } from "@/features/user/user.api";
import { useUserStore } from "@/features/user/user.store";
import { workflowApi } from "@/features/workflow";
import { rootRoute } from "@/routes";
import { ConfirmModal } from "@/shared/ui/composites";
import { Outlet, createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

async function hydrateOAuthSession(tokens: { accessToken: string; refreshToken: string }) {
	useAuthStore.getState().setTokens(tokens);

	try {
		const me = await userApi.getMe();
		return { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, me };
	} catch {
		const refreshed = await authApi.refresh({ refreshToken: tokens.refreshToken });
		useAuthStore.getState().setAccessToken(refreshed.accessToken);
		const me = await userApi.getMe();
		return { accessToken: refreshed.accessToken, refreshToken: tokens.refreshToken, me };
	}
}

export const workflowRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/workflow",
	component: () => <Outlet />,
	beforeLoad: async () => {
		await waitForAuthHydration();

		if (ensureMockAuthSession()) {
			return;
		}

		// Check for OAuth tokens in URL before checking store
		const params = new URLSearchParams(window.location.search);
		const accessToken = params.get("accessToken");
		const refreshToken = params.get("refreshToken");

		if (accessToken && refreshToken) {
			// Process OAuth tokens immediately in beforeLoad
			try {
				const session = await hydrateOAuthSession({ accessToken, refreshToken });

				useAuthStore.getState().setAuth({
					accessToken: session.accessToken,
					refreshToken: session.refreshToken,
					user: {
						id: session.me.id,
						email: session.me.email,
						name: session.me.name,
						plan: session.me.plan,
						gyulBalance: session.me.gyulBalance,
					},
				});
				useUserStore.getState().setMe(session.me);

				// Clean URL parameters
				window.history.replaceState({}, document.title, window.location.pathname);
				return;
			} catch (error) {
				console.error("OAuth token processing failed:", error);
				useAuthStore.getState().clear();
				throw redirect({ to: "/login" });
			}
		}

		const token = useAuthStore.getState().accessToken;
		if (!token) {
			throw redirect({ to: "/login" });
		}

		const plan = useAuthStore.getState().user?.plan;
		if (plan === "FREE") {
			throw redirect({ to: "/pricing" });
		}
	},
});

const ForbiddenFallback = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<ConfirmModal
			isOpen
			onCancel={() => navigate({ to: "/login", replace: true })}
			title={t("workflow.forbiddenTitle")}
			description={t("workflow.forbiddenDescription")}
			onConfirm={() => navigate({ to: "/login", replace: true })}
			isCancelDisabled
		/>
	);
};

const LAST_WORKFLOW_KEY = "last_workflow_id";

export const saveLastWorkflowId = (id: string) => {
	localStorage.setItem(LAST_WORKFLOW_KEY, id);
};

export const workflowIndexRoute = createRoute({
	getParentRoute: () => workflowRoute,
	path: "/",
	component: ForbiddenFallback,
	beforeLoad: async () => {
		if (ensureMockAuthSession()) {
			throw redirect({ to: "/workflow/$id", params: { id: "mock" }, replace: true });
		}

		let workflows: { id: string }[] = [];
		try {
			const data = await workflowApi.list();
			workflows = data.workflows;
		} catch (e) {
			if (e instanceof AxiosError && e.response?.status === 401) {
				throw redirect({ to: "/login" });
			}
			if (e instanceof AxiosError && e.response?.status === 403) {
				return;
			}
		}

		if (workflows.length === 0) {
			throw redirect({ to: "/workflow/new" });
		}

		const lastId = localStorage.getItem(LAST_WORKFLOW_KEY);
		const targetId = lastId && workflows.some((w) => w.id === lastId) ? lastId : workflows[0].id;

		throw redirect({ to: "/workflow/$id", params: { id: targetId }, replace: true });
	},
});
